/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { FunctionComponent, Fragment } from 'react';
import { styled, Theme } from '@material-ui/core/styles';
import { Typography, Box } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import Button from '../../buttons/PGE-Button';
import EmailTextField from '../../email-text-field';
import InstallerLoginHint from './InstallerLoginHint';
import { PageTextContentType } from '../../../hooks/useContentMgmt';
import Backdrop from '../../backdrop';
import Link from '../../text-link';
import useInstallerQuickLogin from '../../../hooks/pge-plus/useInstallerQuickLogin';

const PREFIX = 'InstallerQuickLogin';

const classes = {
  card: `${PREFIX}-card`,
  cardContent: `${PREFIX}-cardContent`,
  cardActions: `${PREFIX}-cardActions`,
  hr: `${PREFIX}-hr`,
  submit: `${PREFIX}-submit`,
  paidMessageBox: `${PREFIX}-paidMessageBox`,
};

const Root = styled('div')(({ theme }: { theme: Theme }) => ({
  [`& .${classes.card}`]: {},
  [`& .${classes.cardContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  [`& .${classes.cardActions}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 0,
  },
  [`& .${classes.hr}`]: {
    border: '0.3px solid black',
    opacity: '0.1',
    margin: theme.spacing(3, 0),
  },
  [`& .${classes.submit}`]: {
    marginTop: 8,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  [`& .${classes.paidMessageBox}`]: {
    marginTop: '1em',
    '& > .MuiCollapse-container > .MuiCollapse-wrapper': {
      '& .MuiCollapse-wrapperInner > .MuiAlert-standardSuccess': {
        backgroundColor: '#fff !important',
      },
    },
  },
}));

type Props = {
  content: PageTextContentType;
};

const InstallerQuickLoginForm: FunctionComponent<Props> = ({ content }) => {
  const {
    quickform,
    quickLoginhandleSubmit,
    errorMessage,
    isLoading,
    requiresEmailConfirmation,
    handleRequestNewLink,
  } = useInstallerQuickLogin({
    successMessageText: content.get('INSTELLER_SIGN_IN_LINK_SENT')!,
    genericErrorText: content.get('GENERIC_ERROR_NOTIFICATION_MESSAGE')!,
    accountLockedText: content.get('GENERIC_ERROR_NOTIFICATION_MESSAGE')!,
  });

  const TITLE_INFO_TEXT = requiresEmailConfirmation
    ? content.get('INSTALLER_SIGN_IN_CONFIRM_EMAIL_INFO')
    : content.get('INSTALLER_SIGN_IN_INFO');
  const BUTTON_TEXT = requiresEmailConfirmation
    ? content.get('CONTINUE')
    : content.get('SUBMIT');

  return (
    <Root>
      <Fragment>
        <Typography component={'div'} variant={'body2'}>
          {TITLE_INFO_TEXT}
        </Typography>

        {errorMessage && (
          <Alert severity="error" css={{ marginTop: 8 }}>
            {errorMessage}
          </Alert>
        )}

        {isLoading && <Backdrop forceOpen />}

        <form noValidate>
          <EmailTextField
            name="email"
            label={content.get('REGISTERED_EMAIL_ADDRESS')}
            variant="outlined"
            autoComplete={'email'}
            style={{ width: '100%' }}
            {...quickform.props('email')}
          />
        </form>
        {!requiresEmailConfirmation && <InstallerLoginHint content={content} />}
        <Box
          display="flex"
          justifyContent={
            requiresEmailConfirmation ? 'space-between' : 'flex-end'
          }
          alignItems="center"
        >
          {requiresEmailConfirmation && (
            <Link
              plain
              onClick={e => {
                e.preventDefault();
                handleRequestNewLink();
              }}
              variant={'body2'}
            >
              Request new signin link
            </Link>
          )}
          <Button
            className={classes.submit}
            type={'submit'}
            variant={'contained'}
            color={'primary'}
            onClick={quickform.submit(quickLoginhandleSubmit)}
          >
            {BUTTON_TEXT}
          </Button>
        </Box>
      </Fragment>
    </Root>
  );
};

export default InstallerQuickLoginForm;
